/**
 * useDataGridMouseDrag
 *
 * Handles click-drag to select a rectangular range of cells,
 * and Shift+Click to extend selection. Auto-scrolls near edges.
 *
 * Plan 124, Epic 02 — Mouse Drag & Shift+Click
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CellPosition } from './types';

// ---------------------------------------------------------------------------
// Options / Return
// ---------------------------------------------------------------------------

export interface UseDataGridMouseDragOptions {
  isEditMode: boolean;
  setRange: (anchor: CellPosition, focus: CellPosition) => void;
  selectCell: (pos: CellPosition) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Current range anchor — kept in sync by orchestrator via ref to avoid stale closures */
  anchorRef: React.RefObject<CellPosition | null>;
}

export interface UseDataGridMouseDragReturn {
  handleCellMouseDown: (pos: CellPosition, event: React.MouseEvent) => void;
  isDragging: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve which cell is under the cursor from data-* attributes */
function resolveCellFromPoint(x: number, y: number): CellPosition | null {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  const td = (el as HTMLElement).closest('[data-row-index]') as HTMLElement | null;
  if (!td) return null;
  const rowIndex = Number(td.dataset.rowIndex);
  const columnId = td.dataset.columnId;
  if (isNaN(rowIndex) || !columnId) return null;
  return { rowIndex, columnId };
}

/** Auto-scroll the container when cursor is near edges */
const AUTO_SCROLL_THRESHOLD = 40;
const AUTO_SCROLL_SPEED = 4;

function autoScroll(container: HTMLElement, clientY: number): void {
  const rect = container.getBoundingClientRect();
  if (clientY - rect.top < AUTO_SCROLL_THRESHOLD) {
    container.scrollTop -= AUTO_SCROLL_SPEED;
  } else if (rect.bottom - clientY < AUTO_SCROLL_THRESHOLD) {
    container.scrollTop += AUTO_SCROLL_SPEED;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDataGridMouseDrag(
  opts: UseDataGridMouseDragOptions,
): UseDataGridMouseDragReturn {
  const { isEditMode, setRange, selectCell, scrollContainerRef, anchorRef } = opts;

  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragAnchorRef = useRef<CellPosition | null>(null);
  const rafId = useRef(0);
  const lastClientPos = useRef({ x: 0, y: 0 });
  const lastResolvedPos = useRef<CellPosition | null>(null);

  // Store latest opts in ref for document-level listeners (avoids stale closures)
  const setRangeRef = useRef(setRange);
  setRangeRef.current = setRange;
  const scrollRef = useRef(scrollContainerRef);
  scrollRef.current = scrollContainerRef;

  // ------ Document-level handlers (stable refs) ------

  const handleDocMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    lastClientPos.current = { x: e.clientX, y: e.clientY };

    if (rafId.current !== 0) return; // rAF already pending
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      if (!isDraggingRef.current) return;

      const { x, y } = lastClientPos.current;
      const resolved = resolveCellFromPoint(x, y);

      if (resolved) {
        lastResolvedPos.current = resolved;
      }

      const pos = resolved ?? lastResolvedPos.current;
      const anchor = dragAnchorRef.current;
      if (!pos || !anchor) return;

      setRangeRef.current(anchor, pos);

      // Auto-scroll
      const container = scrollRef.current?.current;
      if (container) autoScroll(container, y);
    });
  }, []);

  const handleDocMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);

    document.removeEventListener('mousemove', handleDocMouseMove);
    document.removeEventListener('mouseup', handleDocMouseUp);
    document.removeEventListener('contextmenu', handleDocContextMenu);

    if (rafId.current !== 0) {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
    }

    // Remove select-none
    const container = scrollRef.current?.current;
    if (container) container.classList.remove('select-none');
  }, [handleDocMouseMove]);

  const handleDocContextMenu = useCallback(() => {
    // Right-click during drag → cancel drag
    if (isDraggingRef.current) {
      handleDocMouseUp();
    }
  }, [handleDocMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleDocMouseMove);
      document.removeEventListener('mouseup', handleDocMouseUp);
      document.removeEventListener('contextmenu', handleDocContextMenu);
      if (rafId.current !== 0) cancelAnimationFrame(rafId.current);
    };
  }, [handleDocMouseMove, handleDocMouseUp, handleDocContextMenu]);

  // ------ Cell mousedown handler ------

  const handleCellMouseDown = useCallback(
    (pos: CellPosition, event: React.MouseEvent) => {
      if (!isEditMode) return;
      if (event.button !== 0) return; // only left click

      // Shift+Click: extend range from current anchor to clicked cell
      if (event.shiftKey) {
        const currentAnchor = anchorRef.current;
        if (currentAnchor) {
          setRange(currentAnchor, pos);
        }
        event.preventDefault(); // prevent text selection
        return;
      }

      // Start drag
      dragAnchorRef.current = pos;
      lastResolvedPos.current = pos;
      isDraggingRef.current = true;
      setIsDragging(true);

      // Select this cell (sets active cell + collapses range to single cell)
      selectCell(pos);

      // Add document-level listeners
      document.addEventListener('mousemove', handleDocMouseMove);
      document.addEventListener('mouseup', handleDocMouseUp);
      document.addEventListener('contextmenu', handleDocContextMenu);

      // Prevent text selection during drag
      const container = scrollContainerRef?.current;
      if (container) container.classList.add('select-none');
    },
    [isEditMode, anchorRef, setRange, selectCell, scrollContainerRef,
     handleDocMouseMove, handleDocMouseUp, handleDocContextMenu],
  );

  return { handleCellMouseDown, isDragging };
}
